import { AppDispatch } from "@Reducers/index";
import { getBackendUrl } from "@Utils";
import axiosWrapper from "../services/axiosWrapper";
import { objectIdString, type ObjectIdString } from "@Helpers/schemas/common.schema";
import type {
  AddProjectRoleRequest,
  AddProjectRoleResponse,
  DeleteProjectRoleRequest,
  DeleteProjectRoleResponse,
  ChangeProjectRolePermissionRequest,
  ChangeProjectRolePermissionResponse,
  GetProjectRolesRequest,
  GetProjectRolesResponse,
  ChangeUserRoleFromProjectRequest,
  ChangeUserRoleFromProjectResponse,
  RemoveUsersFromSingleProjectRequest,
  RemoveUsersFromSingleProjectResponse,
  GetProjectObjectResponse,
  TeamsControllerInsertRequest,
  TeamsControllerUpdateRequest,
  TeamsControllerDeleteRequest,
  TeamsControllerGetRequest,
  TeamsControllerResponse,
  LocationControllerInsertRequest,
  LocationControllerUpdateRequest,
  LocationControllerDeleteRequest,
  LocationControllerReorderRequest,
  LocationControllerGetRequest,
  LocationControllerResponse,
  WbsControllerInsertRequest,
  WbsControllerUpdateRequest,
  WbsControllerDeleteRequest,
  WbsControllerGetRequest,
  WbsControllerResponse,
} from "@ServerTypes/projects/project.schema";
import type {
  SaveDefaultSpaceRequest,
  SaveDefaultSpaceResponse,
  RemoveSpaceRequest,
  SpacesDataResponse,
} from "@ServerTypes/spaces/space.schema";
import type {
  ProjectTemplateConfigHandlerRequest,
  ProjectTemplateConfigHandlerResponse,
} from "@ServerTypes/forms/forms.schema";
import type {
  EditVisibleDataRequest,
  EditVisibleDataResponse,
  SaveStatusDataRequest,
  SaveStatusDataResponse,
  GetStatusDataRequest,
  GetStatusDataResponse,
  GetCategoryDataResponse,
} from "@ServerTypes/status/status.schema";
import {
  setProjectRolesList,
  setCategoryListAction as setCategoryList,
  setStatusData,
  deletedSpaceStatus,
  getProjectSettingObjectAction as getProjectSettingObject,
} from "../reducers/projectSetting";
import type { Exact } from "@Helpers/types/exact";

export async function addProjectRole<T extends Exact<AddProjectRoleRequest, T>>(
  data: T,
): Promise<AddProjectRoleResponse> {
  const response = await axiosWrapper.post<AddProjectRoleResponse>(`${getBackendUrl()}/projects/addProjectRole`, data);
  return response.data;
}

export async function deleteProjectRole<T extends Exact<DeleteProjectRoleRequest, T>>(
  data: T,
): Promise<DeleteProjectRoleResponse> {
  const response = await axiosWrapper.post<DeleteProjectRoleResponse>(
    `${getBackendUrl()}/projects/deleteProjectRole`,
    data,
  );
  return response.data;
}

// updateProjectRolePermission
export async function updateProjectRolePermission<T extends Exact<ChangeProjectRolePermissionRequest, T>>(data: T) {
  const res = await axiosWrapper.post<ChangeProjectRolePermissionResponse>(
    `${getBackendUrl()}/projects/changeProjectRolePermission`,
    data,
  );
  return res;
}

/** `projectId` becomes `GetProjectRolesRequest["id"]`, an `objectIdString` in projects.schema.ts. */
export function getProjectRoles(data: { projectId?: ObjectIdString }) {
  return (dispatch: AppDispatch) => {
    let projectId = data.projectId;
    if (!projectId) {
      // Fallback entry point: the second-to-last URL path segment is the Project `_id` on the
      // project-settings routes. Path segments are outside the type system, so parse rather than
      // tag — the backend rejects a non 24-hex id anyway, so there is nothing to fetch without one.
      const pathArray = location.pathname.split("/");
      projectId = objectIdString.safeParse(pathArray[pathArray.length - 2]).data;
      if (!projectId) return;
    }
    const requestData: GetProjectRolesRequest = { id: projectId };
    axiosWrapper
      .post<GetProjectRolesResponse>(`${getBackendUrl()}/projects/getProjectRoles`, requestData)
      .then((response) => {
        if (response.data.status) {
          dispatch(setProjectRolesList(response.data.data));
        }
      });
  };
}

export async function saveProjectRole<T extends Exact<ChangeUserRoleFromProjectRequest, T>>(data: T) {
  const res = await axiosWrapper.post<ChangeUserRoleFromProjectResponse>(
    `${getBackendUrl()}/projects/changeUserRoleFromProject/`,
    data,
  );
  return res;
}
// updateStatusSpaces
export function updateStatusSpaces<T extends Exact<EditVisibleDataRequest, T>>(data: T) {
  return () => {
    axiosWrapper.post<EditVisibleDataResponse>(`${getBackendUrl()}/status/editVisibleData`, data);
  };
}
// addStatusToProject
export function addStatusToProject<T extends Exact<SaveStatusDataRequest, T>>(data: T) {
  return () => {
    axiosWrapper.post<SaveStatusDataResponse>(`${getBackendUrl()}/status/saveStatusData`, data);
  };
}
// getStatusData
export function getStatusData<T extends Exact<GetStatusDataRequest, T>>(data: T) {
  return (dispatch: AppDispatch) => {
    axiosWrapper.post<GetStatusDataResponse>(`${getBackendUrl()}/status/getStatusData`, data).then((response) => {
      if (response.data.status) {
        dispatch(setStatusData(response.data.data));
      }
    });
  };
}
// getAllCategories
export function getAllCategories() {
  return (dispatch: AppDispatch) => {
    axiosWrapper.get<GetCategoryDataResponse>(`${getBackendUrl()}/status/getCategoryData`).then((response) => {
      if (response.data.status) {
        dispatch(setCategoryList(response.data.data));
      }
    });
  };
}
// get list of user which are added into project

// export function deleteSpace(data) {
//   return async (dispatch: AppDispatch) => {
//     const response = await axiosWrapper.post(
//       `${getBackendUrl()}/spaces/deleteSpacesData`,
//       data
//     );

//     dispatch(deletedSpaceStatus(response.data.data));
//     return response.data.data.status;
//   };
// }

// delete space from project
/** `spaceId` becomes `RemoveSpaceRequest["key"]["spaceId"]`, an `objectIdString` in spaces.schema.ts. */
export function deleteSpace(data: { spaceId: ObjectIdString }) {
  return (dispatch: AppDispatch) => {
    const requestData: RemoveSpaceRequest = {
      key: { spaceId: data.spaceId },
    };
    axiosWrapper.post<SpacesDataResponse>(`${getBackendUrl()}/spaces/removeSpace`, requestData).then((response) => {
      dispatch(deletedSpaceStatus(response.data.result));
    });
  };
}

// Delete User from a project

export async function removeUsersFromSingleProject<T extends Exact<RemoveUsersFromSingleProjectRequest, T>>(
  data: T,
): Promise<RemoveUsersFromSingleProjectResponse> {
  const response = await axiosWrapper.post<RemoveUsersFromSingleProjectResponse>(
    `${getBackendUrl()}/projects/removeUsersFromSingleProject/`,
    data,
  );
  return response.data;
}

export function getProjectObject(data: { id?: string }) {
  // The `id` guard lives inside the thunk so the creator always returns something
  // dispatchable. It previously returned `undefined` when `id` was absent, which
  // made `dispatch(getProjectObject(...))` throw at runtime for a missing id and
  // is untypeable under react-redux v9's precise ThunkDispatch.
  return (dispatch: AppDispatch) => {
    if (!data.id) return;
    axiosWrapper
      .get<GetProjectObjectResponse>(`${getBackendUrl()}/projects/getProjectObject/${data.id}`)
      .then((res) => {
        if (res.data.status) {
          dispatch(getProjectSettingObject(res.data.data));
        }
      })
      .catch((err: unknown) => {
        console.log("Error in project setting object ", err);
        Promise.reject(err);
      });
  };
}
export type TeamsControllerRequest =
  | TeamsControllerInsertRequest
  | TeamsControllerUpdateRequest
  | TeamsControllerDeleteRequest
  | TeamsControllerGetRequest;

// Each former action-dispatcher is split into dedicated endpoints. These maps route an `action`
// to its endpoint path; an unmapped/absent action falls back to the list endpoint.
const TEAM_ACTION_PATHS: Record<string, string> = {
  insert: "/projects/insertTeam",
  update: "/projects/updateTeam",
  delete: "/projects/removeTeam",
};
const LOCATION_ACTION_PATHS: Record<string, string> = {
  insert: "/projects/insertLocation",
  update: "/projects/updateLocation",
  delete: "/projects/removeLocation",
  reorder: "/projects/reorderLocation",
};
const WBS_ACTION_PATHS: Record<string, string> = {
  insert: "/projects/insertWBS",
  update: "/projects/updateWBS",
  delete: "/projects/removeWBS",
};

export const teamsController = async <T extends Exact<TeamsControllerRequest & { action?: string }, T>>(
  data: T,
): Promise<{ data: TeamsControllerResponse }> => {
  const action = (data as { action?: string }).action;
  const path = (action && TEAM_ACTION_PATHS[action]) || "/projects/teamsController";
  const response = await axiosWrapper.post<TeamsControllerResponse>(`${getBackendUrl()}${path}`, data);
  return response;
};

export type LocationControllerRequest =
  | LocationControllerInsertRequest
  | LocationControllerUpdateRequest
  | LocationControllerDeleteRequest
  | LocationControllerReorderRequest
  | LocationControllerGetRequest;

export const locationController = async <T extends Exact<LocationControllerRequest & { action?: string }, T>>(
  data: T,
): Promise<{ data: LocationControllerResponse }> => {
  const action = (data as { action?: string }).action;
  const path = (action && LOCATION_ACTION_PATHS[action]) || "/projects/locationController";
  const response = await axiosWrapper.post<LocationControllerResponse>(`${getBackendUrl()}${path}`, data);
  return response;
};

export const projectTemplateConfigHandler = async <T extends Exact<ProjectTemplateConfigHandlerRequest, T>>(
  data: T,
): Promise<{ data: ProjectTemplateConfigHandlerResponse }> => {
  const response = await axiosWrapper.post<ProjectTemplateConfigHandlerResponse>(
    `${getBackendUrl()}/forms/projectTemplateConfigHandler`,
    data,
  );
  return response;
};

export type WBSControllerRequest =
  WbsControllerInsertRequest | WbsControllerUpdateRequest | WbsControllerDeleteRequest | WbsControllerGetRequest;

export const WBSController = async <T extends Exact<WBSControllerRequest & { action?: string }, T>>(
  data: T,
): Promise<{ data: WbsControllerResponse }> => {
  const action = (data as { action?: string }).action;
  const path = (action && WBS_ACTION_PATHS[action]) || "/projects/WBSController";
  const response = await axiosWrapper.post<WbsControllerResponse>(`${getBackendUrl()}${path}`, data);
  return response;
};
export const saveDefaultSpace = async <T extends Exact<SaveDefaultSpaceRequest, T>>(
  data: T,
): Promise<{ data: SaveDefaultSpaceResponse }> => {
  return await axiosWrapper.post<SaveDefaultSpaceResponse>(`${getBackendUrl()}/spaces/saveDefaultSpace`, data);
};
